"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../utils/mergeClassNames";

const textVariants = cva("", {
  variants: {
    variant: {
      default: "",
      muted: "opacity-70",
      subtle: "opacity-60",
      strong: "font-semibold",
      bold: "font-bold",
      italic: "italic",
      underline: "underline",
    },
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl",
      "5xl": "text-5xl",
      "6xl": "text-6xl",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "base",
    align: "left",
  },
});

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof textVariants> {
  as?:
    | "p"
    | "span"
    | "div"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "label"
    | "strong"
    | "em"
    | "small";
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, variant, size, align, as = "p", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref as never}
        className={cn(textVariants({ variant, size, align }), className)}
        {...props}
      />
    );
  },
);

Text.displayName = "Text";

export { Text, textVariants };
